import React from 'react';
import { useIntl } from 'react-intl';
import Link from 'next/link';
import { Row, Col } from 'antd';
import { useRouter } from 'next/router';
import { rowThumbnailProps } from 'src/utils/grid';
import { convertToInternalLink } from 'src/utils/url';
import styles from './Cta.module.scss';
import {
  GlobalButton,
  ImageEntity,
  GlobalFormSalesforce,
  GlobalFormBrochures,
  GlobalNewsletterButton,
  GlobalIcon,
  IconImage,
} from 'src/modules/global';

enum CtaOverlayType {
  None = 'none',
  Contact = 'contact',
  Newsletter = 'newsletter',
  SalesNewsletter = 'sales_newsletter',
  Brochures = 'brochures',
}

export interface CtaProps {
  className?: string;
  text: string;
  url: string;
  external_link: boolean;
  isButton: boolean;
  button?: boolean; // This field is for articles-preview as it's a REST endpoint and it's not possible to rename the field to isButton
  overlay: CtaOverlayType;
  download: ImageEntity;
  align?: boolean;
  type?: 'primary' | 'secondary' | 'white';
  widthType?: 'fullWidth' | 'halfWidth' | 'fluid' | 'padded';
}

const Cta: React.FunctionComponent<CtaProps> = (props: CtaProps) => {
  const intl = useIntl();
  const router = useRouter();
  const {
    text,
    url = '#',
    external_link,
    download,
    overlay,
    isButton,
    button,
    align = false,
    type = 'primary',
  } = props;

  const link = download ? download.url : url;

  const isEmail = !!url?.match(/^\w+@[a-zA-Z_]+?\.[a-zA-Z]{2,3}$/);
  const target = external_link ? '_blank' : '';
  let href = isEmail ? `mailto:${url}` : convertToInternalLink(link);

  href = href || '';

  let buttonCta;

  const getButton = () => {
    return isButton || button ? (
      <GlobalButton
        type={type}
        href={href}
        target={target}
        widthType={props.widthType || 'fullWidth'}>
        {text}
      </GlobalButton>
    ) : (
      <>
        <Link href={href} as={href} locale={router.locale}>
          <a rel="noopener noreferrer" href={href} target={target}>
            {text}
          </a>
        </Link>
        <GlobalIcon image={IconImage.Link} className={styles.linkIcon} />
      </>
    );
  };
  if (overlay) {
    switch (overlay) {
      case CtaOverlayType.Brochures:
        buttonCta = (
          <GlobalFormBrochures
            title={intl.formatMessage({ id: 'form/brochures/title' })}
            buttonText={text}
          />
        );
        break;
      case CtaOverlayType.Contact:
        buttonCta = (
          <GlobalFormSalesforce
            title={intl.formatMessage({ id: 'form/title' })}
            buttonText={text}
            fullWidth={false}
          />
        );
        break;
      case CtaOverlayType.Newsletter:
      case CtaOverlayType.SalesNewsletter:
        buttonCta = <GlobalNewsletterButton showInfoText={false} />;
        break;

      case CtaOverlayType.None:
        buttonCta = getButton();
    }
  } else {
    buttonCta = getButton();
  }
  return align ? (
    <Row {...rowThumbnailProps}>
      <Col xs={24} md={{ span: 12, offset: 6 }}>
        {buttonCta}
      </Col>
    </Row>
  ) : (
    buttonCta
  );
};

export default Cta;
